import { z } from "zod";
import { conversationKindSchema } from "../enums.ts";
import { isoDateTimeSchema, pageOf, paginationQuerySchema, uuidSchema } from "./common.ts";

/**
 * Messaging: one private thread per member per business, plus one one-way announcement channel per business.
 * See `teamna/strategy/messaging-v1.md` in the assistant repo for the behaviour these shapes carry.
 */

/** The longest thing anyone can say in one message. Long enough for a term letter, short enough to read on a phone. */
export const MESSAGE_BODY_MAX = 2000;

export const messageBodySchema = z
  .string()
  .trim()
  .min(1, "Write something first")
  .max(MESSAGE_BODY_MAX, `Keep it under ${MESSAGE_BODY_MAX} characters`);

export const messageSchema = z.object({
  id: uuidSchema,
  conversationId: uuidSchema,
  businessId: uuidSchema,
  /** Null once the sender's account is gone. The message stays; the business's record is not theirs to erase. */
  senderUserId: uuidSchema.nullable(),
  /** First name and initial for a person, or the business name when the sender is unknown. Rendered as-is. */
  senderName: z.string(),
  /** True when the caller wrote it. Decides which side of the screen it sits on. */
  mine: z.boolean(),
  body: z.string(),
  createdAt: isoDateTimeSchema,
});
export type Message = z.infer<typeof messageSchema>;

/**
 * One row of an inbox. The same shape serves both sides: a member sees the business's name as the
 * counterpart, staff sees the member's. `canPost` is what hides the composer on an announcement
 * thread for a member, and on any thread for a member whose membership is paused.
 */
export const conversationSummarySchema = z.object({
  id: uuidSchema,
  businessId: uuidSchema,
  businessName: z.string(),
  kind: conversationKindSchema,
  /** The member on a direct thread. Null on the announcement channel. */
  memberUserId: uuidSchema.nullable(),
  /** Who the caller is talking to: the business for a member, the member for staff, the business for announcements. */
  counterpartName: z.string(),
  /** The member's phone on a direct thread, staff view only. Null for members and on announcements. */
  counterpartPhoneE164: z.string().nullable(),
  lastMessage: messageSchema.nullable(),
  unreadCount: z.number().int().min(0),
  canPost: z.boolean(),
  createdAt: isoDateTimeSchema,
});
export type ConversationSummary = z.infer<typeof conversationSummarySchema>;

/** `GET /v1/me/conversations` */
export const conversationsResponseSchema = z.object({
  items: z.array(conversationSummarySchema),
});
export type ConversationsResponse = z.infer<typeof conversationsResponseSchema>;

/** `GET /v1/conversations/:id/messages`. Newest first; `cursor` walks back in time. */
export const messagesQuerySchema = paginationQuerySchema;
export const messagesPageSchema = pageOf(messageSchema);
export type MessagesPage = z.infer<typeof messagesPageSchema>;

/** `POST /v1/conversations/:id/messages` */
export const sendMessageRequestSchema = z.object({ body: messageBodySchema });
export type SendMessageRequest = z.infer<typeof sendMessageRequestSchema>;

/**
 * `POST /v1/conversations/:id/read`. Same rule as the notification feed: the client sends the
 * timestamp of the newest message it has actually rendered, so a message that arrives while the
 * thread is open stays unread.
 */
export const markConversationReadRequestSchema = z.object({ through: isoDateTimeSchema });
export type MarkConversationReadRequest = z.infer<typeof markConversationReadRequestSchema>;

/** `POST /v1/owner/businesses/:businessId/conversations/direct` */
export const openDirectConversationRequestSchema = z.object({ memberUserId: uuidSchema });
export type OpenDirectConversationRequest = z.infer<typeof openDirectConversationRequestSchema>;

/** `GET /v1/owner/businesses/:businessId/announcements/audience` */
export const announcementAudienceSchema = z.object({ recipientCount: z.number().int().min(0) });
export type AnnouncementAudience = z.infer<typeof announcementAudienceSchema>;

/** `POST /v1/owner/businesses/:businessId/announcements` */
export const sendAnnouncementRequestSchema = z.object({ body: messageBodySchema });
export type SendAnnouncementRequest = z.infer<typeof sendAnnouncementRequestSchema>;

export const sendAnnouncementResponseSchema = z.object({
  message: messageSchema,
  conversationId: uuidSchema,
  recipientCount: z.number().int().min(0),
});
export type SendAnnouncementResponse = z.infer<typeof sendAnnouncementResponseSchema>;
